import {
  getQueryParams,
  parseTableQueryParams,
  useTableStateReducer,
  type DataTableRow,
  type TableState,
  type TableStateAction,
} from "@wayflyer/flyui-dataviz";
import { isEqual, omit } from "lodash";
import { stringify } from "qs";
import { useEffect, useRef, type Dispatch } from "react";
import { useLocation, useNavigate, useNavigationType, useSearchParams } from "react-router";

/*
  On initial mount, apply any filters that are present in the URL

  On update of table-state, update the query to reflect changes
*/
export const useUrlSyncedTableState = <TRow extends DataTableRow<TRow>>({
  defaultTableState: configuredDefaultTableState,
  queryableColumns,
  ignoredQueryParams = [] as string[],
}): {
  tableState: TableState<TRow>;
  dispatchTableStateChange: Dispatch<TableStateAction<TRow>>;
  ignoredQueryParams?: string[];
} => {
  const navigationType = useNavigationType();
  const { andFilterableColumns } = configuredDefaultTableState ?? {};
  const { search, pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const isInitialLoad = useRef(true);
  const isPop = useRef(navigationType === "POP");
  const navigate = useNavigate();

  // initialise table-state with filters from URL, if present
  let initialQueryPresent = !!search;
  if (ignoredQueryParams && ignoredQueryParams.length > 0) {
    initialQueryPresent = false;
    for (const key of Array.from(searchParams.keys())) {
      if (key && !ignoredQueryParams.includes(key)) {
        initialQueryPresent = true;
        break;
      }
    }
  }

  const { tableState: partialStateFromUrl } = parseTableQueryParams(search, queryableColumns);
  const tableStateFromUrl = { ...partialStateFromUrl, andFilterableColumns };
  const defaultTableState = initialQueryPresent ? tableStateFromUrl : configuredDefaultTableState;
  const { tableState, dispatchTableStateChange } = useTableStateReducer<TRow>({ defaultTableState });

  useEffect(() => {
    const { q, page_number, page_size } = getQueryParams(tableState, queryableColumns);
    const search = stringify({
      ...Object.fromEntries(new URLSearchParams(location.search)),
      q: JSON.stringify(q),
      page_number,
      page_size,
    });
    if (isInitialLoad.current && !initialQueryPresent) {
      // sync the URL with the table state
      isInitialLoad.current = false;
      isPop.current = false;
      navigate(
        {
          pathname,
          search,
        },
        {
          replace: true,
        }
      );
    } else if (!isEqual(tableStateFromUrl, omit(tableState, "andFilterableColumns"))) {
      // table state has changed, sync with URL
      // Use replace if we arrived via back/forward to preserve history
      navigate(
        {
          pathname,
          search,
        },
        isPop.current ? { replace: true } : undefined
      );
      isPop.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  return { tableState: navigationType === "POP" ? tableStateFromUrl : tableState, dispatchTableStateChange };
};
